import {
  Component,
  Input,
  ViewEncapsulation,
  ViewChild,
  SimpleChanges,
  OnChanges,
  AfterViewInit,
  HostListener,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatGridListModule } from '@angular/material/grid-list';
import { TemplateGeneratorComponent } from '../template-generator/template-generator.component';
import { DynamicComponentComponent } from '../dynamic-component/dynamic-component.component';
import {
  CompactType,
  DisplayGrid,
  Gridster,
  GridsterConfig,
  GridsterItem,
  GridsterItemConfig,
  GridType,
} from 'angular-gridster2';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ElementRef } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { ChangeDetectorRef } from '@angular/core';
import { TemplateService } from '../../services/template/template.service';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { DragService } from '../../services/drag/drag.service';
import { CustomComponent } from '../../models/CustomComponent.model';
import { CustomComponentDTO } from '../../dto/component/customComponent.dto';
@Component({
  selector: 'app-template-generator-dynamic',
  standalone: true,
  imports: [
    MatGridListModule,
    DynamicComponentComponent,
    Gridster,
    GridsterItem,
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
  ],
  templateUrl: './template-generator-dynamic.component.html',
  styleUrl: './template-generator-dynamic.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class TemplateGeneratorDynamicComponent implements OnChanges {
  //Input mode, 3 readonly options, desktop = 1, tablet = 2, mobile = 3
  selectedCustomComponentDynamicName: string | null = null;

  @Input() mode: 1 | 2 | 3 = 1;

  @Input() parentComponent: TemplateGeneratorComponent;

  @ViewChild('previewContainer') previewContainer!: ElementRef;
  copyComponentsFromMode: any;

  gridOptions: GridsterConfig = {};

  constructor(
    parentComponent: TemplateGeneratorComponent,
    private breakpointObserver: BreakpointObserver,
    private changeDetectorRef: ChangeDetectorRef,
    private templateService: TemplateService,
    private dragService: DragService
  ) {
    this.parentComponent = parentComponent;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['mode']) {
      //this.updateViewportSize();
    }
  }

  ngOnInit() {
    this.gridOptions = {
      gridType: GridType.VerticalFixed,
      compactType: CompactType.None,
      margin: 0,
      outerMargin: false,
      draggable: {
        enabled: true,
        ignoreContent: true, //This is what causes the dragging issue when set to true
        dragHandleClass: 'drag-handle',
        ignoreContentClass: 'content-area',
      },
      resizable: {
        enabled: true,
      },
      pushItems: false,
      pushResizeItems: false,
      swap: false,
      minCols: 12,
      maxCols: 12,
      minItemRows: 1,
      maxItemRows: 200,
      minItemCols: 1,
      maxItemCols: 12,
      minRows: 200,
      maxRows: Infinity,
      rowHeight: 50,
      fixedRowHeight: 50,
      setGridSize: false,
      useTransformPositioning: true,
      mobileBreakpoint: 10,
      displayGrid: DisplayGrid.Always,
      enableEmptyCellClick: true,
      emptyCellDrag: true,
      keepFixedHeightInMobile: true,
      keepFixedWidthInMobile: false,
      enableOccupiedCellDrop: true,
      dragEnabled: true,
      dragAndDrop: true,
      enableEmptyCellDrop: true,
      itemChangeCallback: (item: GridsterItemConfig) => {
        this.changeDetectorRef.detectChanges();
      },
      itemResizeCallback: (item: GridsterItemConfig) => {
        this.changeDetectorRef.detectChanges();
      },
      optionsChanged: () => {
        this.changeDetectorRef.detectChanges();
      },
    };

    // Listen for component dropped events from containers
    document.addEventListener('componentDropped', ((event: CustomEvent) => {
      const { sourceUUID, containerUUID, x, y, mode } = event.detail;

      this.parentComponent.moveComponentToContainer(
        sourceUUID,
        containerUUID,
        mode,
        x,
        y
      );
    }) as EventListener);
  }

  ngOnDestroy() {
    // Clean up event listener
    document.removeEventListener('componentDropped', ((event: CustomEvent) => {
      // Same function as above
    }) as EventListener);
  }

  onDropToChild(event: any) {
  }

  onDragStop(event: any) {
  }

  onDragStart(event: DragEvent, item: CustomComponent) {

    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      const dragData = {
        ...CustomComponent.convertToDTO(item),
        componentName: item.name,
        sourceUUID: item.uuid,
        parentUUID:
          this.parentComponent.selectedCustomTemplatesDynamicUUID[this.mode],
        isComponentContainer: item.name === 'ComponentContainerComponent',
        isChild: item.parentUUID !== undefined,
        childComponents: item.childComponents?.map((child) =>
          CustomComponent.convertToDTO(child)
        ),
      };

      event.dataTransfer.setData('text/plain', JSON.stringify(dragData));
      this.dragService.setDragData(dragData);
    }
  }

  onDragEnd(event: DragEvent) {
    this.dragService.clearDragData();
  }

  onDragEnter(event: DragEvent) {
    event.preventDefault();
    const dropZone = event.currentTarget as HTMLElement;
    dropZone?.classList.add('drag-over');
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    const dropZone = event.currentTarget as HTMLElement;
    dropZone?.classList.remove('drag-over');
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    const dragData = this.dragService.getDragData();
    if (!dragData) {
      return;
    }

    // Get the actual target container UUID
    const targetElement = event.target as HTMLElement;
    let targetContainerUUID = targetElement.getAttribute('data-container-uuid');

    // If no direct container UUID found, look for the closest gridster container
    if (!targetContainerUUID) {
      const gridsterContainer = targetElement.closest('[data-container-uuid]');
      targetContainerUUID =
        gridsterContainer?.getAttribute('data-container-uuid') || '';
    }

    // If still no container UUID, assume it's the main gridster
    if (!targetContainerUUID) {
      targetContainerUUID =
        this.parentComponent.selectedCustomTemplatesDynamicUUID[this.mode]!;
    }

    // Prevent dropping on itself or its current parent
    if (
      dragData.uuid === targetContainerUUID ||
      (dragData.parentUUID === targetContainerUUID &&
        targetContainerUUID !==
          this.parentComponent.selectedCustomTemplatesDynamicUUID[this.mode])
    ) {
      return;
    }

    // Move the component
    this.moveWithinContainer(dragData, targetContainerUUID, event);
  }

  private moveToMainContainer(dragData: any, event: DragEvent) {
    const targetElement = event.target as HTMLElement;
    const gridster = targetElement.closest('gridster');
    const rect = gridster?.getBoundingClientRect();
    const x = rect
      ? Math.floor((event.clientX - rect.left) / (rect.width / 12))
      : 0;
    const y = rect ? Math.floor((event.clientY - rect.top) / 100) : 0;

    this.parentComponent.moveComponentToContainer(
      dragData.uuid,
      this.parentComponent.selectedCustomTemplatesDynamicUUID[this.mode]!,
      this.mode,
      x,
      y
    );
  }

  private moveWithinContainer(
    dragData: any,
    targetContainerUUID: string,
    event: DragEvent
  ) {

    const targetElement = event.target as HTMLElement;
    const container = document.querySelector(
      `[data-container-uuid="${targetContainerUUID}"]`
    );
    const rect = container?.getBoundingClientRect();
    const x = rect
      ? Math.floor((event.clientX - rect.left) / (rect.width / 12))
      : 0;
    const y = rect ? Math.floor((event.clientY - rect.top) / 100) : 0;

    // Move the component
    this.parentComponent.moveComponentToContainer(
      dragData.uuid,
      targetContainerUUID,
      this.mode,
      x,
      y
    );
  }

  private isContainer(uuid: string): boolean {
    // Add logic to check if the component with this UUID is a container
    return false; // Implement your container check logic here
  }

  /* ngAfterViewInit() {
    this.updateViewportSize();
  }

  private updateViewportSize() {
    if (this.previewContainer) {
      const { width, height } = this.parentComponent.getViewportSize(this.mode);
      this.previewContainer.nativeElement.style.width = `${width}px`;
      this.previewContainer.nativeElement.style.height = `${height}px`;
      
      // Update the custom viewport size in the TemplateService
      this.templateService.setCustomViewportSize(width, height);

      // Force a resize event
      window.dispatchEvent(new Event('resize'));

      // Trigger change detection
      this.changeDetectorRef.detectChanges();
    }
  } */

  @HostListener('removeFromParent', ['$event'])
  onRemoveFromParent(event: Event) {
    const e = event as CustomEvent;
    const uuid = e.detail.uuid;
    if (uuid) {
      this.parentComponent.removeComponentFromGridster(
        this.parentComponent
          .getSelectedTemplateComponents(
            this.parentComponent.selectedCustomTemplatesDynamicUUID[this.mode],
            this.mode
          )
          .find((comp) => comp.uuid === uuid)!,
        this.parentComponent.selectedCustomTemplatesDynamicUUID[this.mode]
      );
    }
  }

  @HostListener('updateChildren', ['$event'])
  onUpdateChildren(event: Event) {
    const e = event as CustomEvent<{
      containerUUID: string;
      childComponents: CustomComponent[];
      mode: number;
    }>;
    this.parentComponent.updateComponentChildren(
      e.detail.containerUUID,
      e.detail.childComponents,
      e.detail.mode
    );
  }

  @HostListener('componentDropped', ['$event'])
  onComponentDropped(event: Event) {
    const { sourceUUID, containerUUID, x, y, mode } = (event as CustomEvent)
      .detail;

    // Use the parent's moveComponentToContainer method directly
    this.parentComponent.moveComponentToContainer(
      sourceUUID,
      containerUUID,
      mode,
      x,
      y
    );

    // Force change detection
    this.changeDetectorRef.detectChanges();
  }

  private handleDrop(dragData: any, event: DragEvent) {
    const targetElement = event.target as HTMLElement;
    const targetContainer = targetElement.closest('[data-container-uuid]');
    const targetContainerUUID = targetContainer?.getAttribute(
      'data-container-uuid'
    );

    // If no target container found, try moving to main container
    if (!targetContainer || !targetContainerUUID) {
      this.moveToMainContainer(dragData, event);
      return;
    }

    // Prevent dropping on self or current parent if not moving within same container
    if (
      dragData.uuid === targetContainerUUID ||
      (dragData.parentUUID === targetContainerUUID &&
        !targetElement.closest('.gridster-item'))
    ) {
      return;
    }

    // Handle drop within container
    this.moveWithinContainer(dragData, targetContainerUUID, event);
  }

  copyComponent(component: CustomComponent) {
    this.parentComponent.copyComponentInTemplate(
      component,
      this.parentComponent.selectedCustomTemplatesDynamicUUID[this.mode] || null
    );
  }
}
